import { useQuery } from "@tanstack/react-query";
import type React from "react";
import { useEffect, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useNavigate } from "react-router";

import type {
  MassMessageCreatedEntityDto,
  MessageCreateRequest,
  MessageMultipleProjectsCreateRequest,
  SimpleProjectDto,
  SystemGroupDto,
} from "@/api/types";
import { Breadcrumbs } from "@/components/Breadcrumbs/Breadcrumbs";
import { Button } from "@/components/Button/Button";
import { ConfirmModal } from "@/components/ConfirmModal/ConfirmModal";
import { CreatePostAppPreview } from "@/components/CreatePostAppPreview/CreatePostAppPreview";
import type { FormDocument } from "@/components/DocumentInput/useDocumentInput";
import { ErrorPage } from "@/components/Error/ErrorPage";
import { Form } from "@/components/Form/Form";
import { FullSizeLoader } from "@/components/FullSizeLoader/FullSizeLoader";
import { FileQuestion01Icon } from "@/components/Icons/FileQuestion01Icon";
import { MessageSmileSquareIcon } from "@/components/Icons/MessageSmileSquareIcon";
import { PencilLineIcon } from "@/components/Icons/PencilLineIcon";
import { Settings04Icon } from "@/components/Icons/Settings04Icon";
import type { FormImage } from "@/components/ImageInput/useImageInput";
import { PageGrid } from "@/components/PageGrid/PageGrid";
import { DocumentPaper } from "@/components/Paper/DocumentPaper";
import type { FormVideo } from "@/components/VideoInput/useVideoInput";
import { useSessionUser } from "@/hooks/Network/useSessionUser";
import { useUploadDocument } from "@/hooks/Network/useUploadDocument";
import { useUploadImage } from "@/hooks/Network/useUploadImage";
import { useUploadVideo } from "@/hooks/Network/useUploadVideo";
import { useBool } from "@/hooks/useBool";
import { useSlug } from "@/hooks/useSlug";
import { AdminCreatePostFormAIHelper } from "@/modules/messages/pages/AdminCreatePost/components/AdminCreatePostFormAIHelper";
import { AdminCreatePostFormEditor } from "@/modules/messages/pages/AdminCreatePost/components/AdminCreatePostFormEditor";
import { AdminCreatePostFormScheduleSelector } from "@/modules/messages/pages/AdminCreatePost/components/AdminCreatePostFormScheduleSelector";
import { AdminCreatePostFormTypeSelector } from "@/modules/messages/pages/AdminCreatePost/components/AdminCreatePostFormTypeSelector";
import { AdminCreatePostSection } from "@/modules/messages/pages/AdminCreatePost/components/AdminCreatePostSection";
import { AdminCreatePostTipsModal } from "@/modules/messages/pages/AdminCreatePost/components/AdminCreatePostTipsModal";
import { communityFeedMutations, useCommunityFeedQueries } from "@/queries/communityFeed";
import { generalMutations } from "@/queries/general";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";

import { CreateMassPostFormAudienceSelector } from "./components/CreateMassPostFormAudienceSelector";
import { CreateMassPostSuccessModal } from "./components/CreateMassPostSuccessModal";

export type AdminCreateMassPostFormValues = {
  postType: MessageMultipleProjectsCreateRequest["type"] | undefined;
  title: string;
  content: string;
  images: FormImage[];
  documents: FormDocument[];
  videos: FormVideo[];
  group: SystemGroupDto | undefined;
  projects: SimpleProjectDto[];
  scheduleDate: Date | undefined;
};
export function CreateMassPostPage(): React.ReactNode {
  // TODO: Replace with a better history-management solution
  const [originalText, setOriginalText] = useState("");
  const [copilotText, setCopilotText] = useState("");
  const [isCopilotText, setIsCopilotText] = useState(false);

  const [audienceReached, setAudienceReached] = useState<MassMessageCreatedEntityDto | undefined>(undefined);
  const [isSuccessModalOpened, successModalHandler] = useBool(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCancelModalOpened, cancelModalHandler] = useBool(false);
  const [isTipsModalOpened, tipsModalHandler] = useBool(false);
  const [isConfirmModalOpen, confirmModalHandler] = useBool(false);

  const navigate = useNavigate();
  const { t } = useTranslation();
  const slug = useSlug();
  const sessionUser = useSessionUser();
  const { uploadFormImage } = useUploadImage();
  const { uploadFormVideo, abortUploadVideos } = useUploadVideo({});
  const { uploadFormDocument } = useUploadDocument();
  const copilotResponseMutation = generalMutations.useGetCopilotResponse();
  const addMassMessageMutation = communityFeedMutations.useAddMassMessage();
  const communityFeedQueries = useCommunityFeedQueries();
  const {
    data: massPostPermissions,
    isPending: isPendingMassPostPermissions,
    error: errorMassPostPermissions,
  } = useQuery(communityFeedQueries.massMessagePermissions());
  let allowedPostTypes: MessageMultipleProjectsCreateRequest["type"][] = [];
  if (massPostPermissions?.canMassMessageAnnouncementPost) {
    allowedPostTypes = ["announcementReadOnly", "announcement"];
  } else if (massPostPermissions?.canMassMessageGeneralPost) {
    allowedPostTypes = ["undefined"];
  }
  const form = useForm<AdminCreateMassPostFormValues>({
    defaultValues: {
      title: "",
      content: "",
      postType: allowedPostTypes.length === 1 ? allowedPostTypes[0] : undefined,
      group: undefined,
      projects: [],
      images: [],
      videos: [],
      documents: [],
      scheduleDate: undefined,
    },
  });
  const currPostType = useWatch({
    name: "postType",
    control: form.control,
  });
  const currTitle = useWatch({
    name: "title",
    control: form.control,
  });
  const currContent = useWatch({
    name: "content",
    control: form.control,
  });
  const currImages = useWatch({
    name: "images",
    control: form.control,
  });
  const currVideos = useWatch({
    name: "videos",
    control: form.control,
  });
  const currDocuments = useWatch({
    name: "documents",
    control: form.control,
  });
  const currGroup = useWatch({
    name: "group",
    control: form.control,
  });
  const currProjects = useWatch({
    name: "projects",
    control: form.control,
  });
  const currScheduleDate = useWatch({
    name: "scheduleDate",
    control: form.control,
  });

  useEffect(() => {
    form.register("projects", {
      validate: (value) => {
        if (value.length === 0) {
          return t("page.admin-create-post.form.projects.error");
        }
      },
    });
  }, [form, t]);

  const reset = () => {
    form.reset();
    setAudienceReached(undefined);
    setCopilotText("");
    setOriginalText("");
    setIsCopilotText(false);
    setIsSubmitting(false);
  };

  const submit = async (formValues: AdminCreateMassPostFormValues) => {
    setIsSubmitting(true);

    const imageUploadPromises = formValues.images.map((image) => uploadFormImage(image));
    // Curently only 1 document is allowed
    const documentUploadPromises = formValues.documents.map((document) => uploadFormDocument(document));
    // Curently only 1 video is allowed
    const videoUploadPromises = formValues.videos.map((video) => uploadFormVideo(video));
    const imageIds = (await Promise.allSettled(imageUploadPromises))
      .map((image) => (image.status === "fulfilled" ? image.value?.id : ""))
      .filter(Boolean) as string[];
    const documentIds = (await Promise.allSettled(documentUploadPromises))
      .map((document) => (document.status === "fulfilled" ? document.value?.id : ""))
      .filter(Boolean) as string[];
    const videoIds = (await Promise.allSettled(videoUploadPromises.map((video) => video)))
      .map((video) => (video.status === "fulfilled" ? video.value?.id : ""))
      .filter(Boolean) as string[];

    try {
      // Only create message when all attachments were uploaded successfully
      if (
        imageIds.length === formValues.images.length &&
        documentIds.length === formValues.documents.length &&
        videoIds.length === formValues.videos.length
      ) {
        // Override post type to standard post if user does not have announcement permissions
        const data: MessageMultipleProjectsCreateRequest = {
          type: formValues.postType!,
          title: formValues.title,
          content: formValues.content,
          imageIds,
          videoIds,
          documentIds,
          systemGroupId: formValues.group?.id,
          projectSelected: formValues.projects.map((project) => project.id),
          scheduledFor: formValues.scheduleDate?.toISOString(),
        };
        const res = await addMassMessageMutation.mutateAsync({
          data,
        });

        setAudienceReached(res);
        successModalHandler.setTrue();
        form.reset();
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCreatePost = async () => {
    await submit(form.getValues());

    confirmModalHandler.setFalse();
  };

  const handleCancel = () => {
    // Cancel all ongoing video uploads
    abortUploadVideos(form.getValues("videos"));

    void navigate(
      routes.messageFeed.list({
        slug,
      }),
    );
  };

  const handleGenerateContent = async () => {
    const content = form.getValues("content");
    const result = await copilotResponseMutation.mutateAsync({
      input: content,
    });
    setOriginalText(content);
    setCopilotText(result.generatedText);
    setIsCopilotText(true);
    form.setValue("content", result.generatedText, {
      shouldDirty: true,
    });
  };

  const handleUndoContent = () => {
    form.setValue("content", originalText, {
      shouldDirty: true,
    });
    setIsCopilotText(false);
  };

  const handleRedoContent = () => {
    form.setValue("content", copilotText);
    setIsCopilotText(true);
  };

  const handleSelectPostType = (newPostType: MessageCreateRequest["type"]) => {
    form.setValue("postType", newPostType, {
      shouldDirty: true,
      shouldValidate: true,
    });

    // Trigger revalidation if the user changes the post type after selecting an audience
    if (form.formState.dirtyFields["projects"])
      form.setValue("projects", [], {
        shouldValidate: true,
      });
  };

  const handleSelectAudience = ({ group, projects }: { group: SystemGroupDto; projects: SimpleProjectDto[] }) => {
    form.setValue("group", group, {
      shouldDirty: true,
    });
    form.setValue("projects", projects, {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  const handleSelectScheduleDate = (date: Date | undefined) => {
    form.setValue("scheduleDate", date, {
      shouldDirty: true,
    });
  };

  if (isPendingMassPostPermissions) {
    return <FullSizeLoader withPadding />;
  }

  if (errorMassPostPermissions) {
    return <ErrorPage error={errorMassPostPermissions} />;
  }

  const isUndoable = isCopilotText;
  const isRedoable = Boolean(copilotText) && !isCopilotText;
  const isCopilotAvailable = currContent.trim().split(" ").length >= 3;

  return (
    <>
      <DocumentPaper
        title={t("page.create-mass-message.title")}
        subTitle={
          <Breadcrumbs
            pages={[
              {
                name: t("page.portfolio.title"),
                to: routes.portfolio.overview(),
              },
              {
                name: t("page.create-mass-message.title"),
              },
            ]}
          />
        }
      >
        <PageGrid.Grid>
          <PageGrid.Item size="75%" isDesktopIndifferent>
            <Form formMethods={form} onSubmit={() => confirmModalHandler.setTrue()}>
              <div className="flex w-full flex-col gap-6">
                {/* Post type */}
                <AdminCreatePostSection
                  title={t("page.admin-create-post.section.step-1.title")}
                  icon={<FileQuestion01Icon size={24} />}
                >
                  <AdminCreatePostFormTypeSelector
                    allowedTypes={allowedPostTypes}
                    selectedType={currPostType}
                    onSelectType={handleSelectPostType}
                  />
                </AdminCreatePostSection>
                {/* Post content */}
                <AdminCreatePostSection
                  title={t("page.admin-create-post.section.step-2.title")}
                  icon={<PencilLineIcon size={24} />}
                >
                  <AdminCreatePostFormAIHelper
                    isUndoable={isUndoable}
                    isRedoable={isRedoable}
                    isCopilotAvailable={isCopilotAvailable}
                    isGenerating={copilotResponseMutation.isPending}
                    onGenerate={handleGenerateContent}
                    onUndo={handleUndoContent}
                    onRedo={handleRedoContent}
                  />
                  <AdminCreatePostFormEditor />
                  {/* App preview (Mobile) */}
                  <div className="mt-4 flex flex-wrap items-center gap-2 xl:hidden">
                    <Button icon={<MessageSmileSquareIcon />} styling="tertiary" onClick={tipsModalHandler.setTrue}>
                      {t("page.admin-create-post.form.btn.show-tips")}
                    </Button>
                    <CreatePostAppPreview
                      title={currTitle}
                      content={currContent}
                      images={currImages}
                      documents={currDocuments}
                      videos={currVideos}
                      groupName={currGroup?.translations?.find((l) => l.languageId === sessionUser.language.id)?.name}
                      postType={
                        massPostPermissions.canMassMessageAnnouncementPost
                          ? currPostType || "announcement"
                          : "undefined"
                      }
                    />
                  </div>
                </AdminCreatePostSection>
                {/* Post audience */}
                <AdminCreatePostSection
                  icon={<Settings04Icon size={24} />}
                  title={t("page.admin-create-post.section.step-3.title")}
                >
                  <CreateMassPostFormAudienceSelector
                    selectedPostType={currPostType}
                    selectedGroup={currGroup}
                    selectedProjects={currProjects}
                    isError={!!form.formState.errors.projects}
                    onConfirmSelection={handleSelectAudience}
                  />
                </AdminCreatePostSection>
                {/* Footer */}
                <AdminCreatePostSection>
                  <div className="flex w-full items-center gap-2">
                    <Button styling="secondary" className="mr-auto" onClick={cancelModalHandler.setTrue}>
                      {t("common.action.cancel")}
                    </Button>
                    <AdminCreatePostFormScheduleSelector
                      selectedDate={currScheduleDate}
                      onSelectDate={handleSelectScheduleDate}
                    />
                    <Button type="submit">
                      {currScheduleDate
                        ? t("page.admin-create-mass-message.form.btn.cta-schedule")
                        : t("page.admin-create-mass-message.form.btn.cta-post")}
                    </Button>
                  </div>
                </AdminCreatePostSection>
              </div>
            </Form>
          </PageGrid.Item>

          {/* App preview (Desktop) */}
          <PageGrid.Item size="25%" isDesktopIndifferent>
            <div className="hidden xl:block">
              <AdminCreatePostSection>
                <CreatePostAppPreview
                  title={currTitle}
                  content={currContent}
                  images={currImages}
                  documents={currDocuments}
                  videos={currVideos}
                  groupName={currGroup?.translations?.find((l) => l.languageId === sessionUser.language.id)?.name}
                  postType={
                    massPostPermissions.canMassMessageAnnouncementPost ? currPostType || "announcement" : "undefined"
                  }
                />
                <Button
                  className="mx-auto"
                  icon={<MessageSmileSquareIcon />}
                  styling="tertiary"
                  onClick={tipsModalHandler.setTrue}
                >
                  {t("page.admin-create-post.form.btn.show-tips")}
                </Button>
              </AdminCreatePostSection>
            </div>
          </PageGrid.Item>
        </PageGrid.Grid>
      </DocumentPaper>
      <ConfirmModal
        title={t("page.admin-create-post.cancel-modal.title")}
        description={t("page.admin-create-post.cancel-modal.description")}
        isOpened={isCancelModalOpened}
        shouldCloseOnEsc
        onReject={cancelModalHandler.setFalse}
        onOpenChange={cancelModalHandler.set}
        rejectBtnProps={{
          text: t("common.action.cancel"),
        }}
        onResolve={handleCancel}
        resolveBtnProps={{
          text: t("common.action.confirm"),
        }}
        isLoading={false}
      />
      <AdminCreatePostTipsModal isOpened={isTipsModalOpened} onOpenChange={tipsModalHandler.set} />
      <ConfirmModal
        title={t("page.create-mass-message.confirm-modal.title")}
        description={t("page.create-mass-message.confirm-modal.description-count", {
          count: form.getValues("projects")?.length || 0,
        })}
        isLoading={isSubmitting}
        onReject={confirmModalHandler.setFalse}
        onOpenChange={confirmModalHandler.set}
        onResolve={handleCreatePost}
        resolveBtnProps={{
          text: t("common.action.confirm"),
          "data-testid": "confirm-btn",
        }}
        isOpened={isConfirmModalOpen}
        data-testid="mass-message-confirm-modal"
        shouldCloseOnEsc
      />
      <CreateMassPostSuccessModal
        audienceReached={audienceReached}
        isOpened={isSuccessModalOpened}
        onOpenChange={(state) => {
          if (!state) {
            reset();
          }

          successModalHandler.set(state);
        }}
      />
    </>
  );
}
